/*
 * Cart drawer — free gift with purchase auto-add/remove.
 *
 * Reads the active promotion config from #gift-promotion-config (rendered
 * inline by cart-drawer.liquid). When the cart's qualifying total crosses
 * the threshold, POSTs the gift variant to /cart/add.js with a _free_gift
 * line item property. When it drops below, removes the managed gift line.
 * Either way, applies the returned sections to the drawer + cart bubble.
 *
 * Server-side enforcement is a matching Shopify "Buy X Get Y" automatic
 * discount with a minimum purchase amount, market-scoped. This JS provides
 * the UX; the discount is what actually makes the gift free at checkout.
 *
 * ONE source for the numbers. The decision reads the [data-cart-state] stamp
 * that cart-drawer.liquid renders into every cart-drawer section
 * (data-qualifying-total, data-gift-key, data-gift-qty,
 * data-gift-variant-in-cart). qualifying_total is the figure the progress
 * banner uses: cart total minus the gift line, non-shipping lines (gift
 * cards, membership) and lines out of stock in this market. Earlier the
 * pubsub path compared the raw cart.total_price and the stepper path
 * subtracted only the gift, so a "FREE" gift could be added or kept in a
 * cart the banner (and the discount minimum) said didn't qualify.
 *
 * Every mutation runs through window.ebCartMutex (assets/cart.js), shared
 * with the drawer's quantity changes, so two chains can never act on the
 * same cart snapshot (which used to add the gift twice).
 *
 * CLEANUP MODE ({"cleanup": true}, no "active"). Rendered when NO promotion
 * is active for this visitor but the cart still holds a `_free_gift` line
 * from an earlier visit (the campaign ended, a market switch, a B2B login).
 * Without it that line sat in the cart at retail and was charged at
 * checkout, since this script — the only thing that removes it — wasn't
 * loaded. In this mode the script only removes that automation-owned line
 * (data-gift-key on the stamp: the `_free_gift` line, never one the shopper
 * added) when the drawer opens, or holds Check out until it's gone, and
 * says so through the drawer's visually-hidden live region. It never adds.
 */
(function () {
  'use strict';

  if (!window.routes || !window.routes.cart_url) return;

  var configEl = document.getElementById('gift-promotion-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  var cleanupOnly = !!(config && config.cleanup && !config.active);
  if (!cleanupOnly && (!config || !config.active || !config.variantId || !config.thresholdCents)) return;

  var GIFT_PROPERTY_KEY = '_free_gift';
  var GIFT_PROPERTY_VALUE = 'true';
  var RECONCILE_DEBOUNCE_MS = 300;

  // True if the promo's end-time (Liquid `ends_at`) is in the past for this
  // visitor's clock. Shopify edge-caches the HTML for anonymous traffic, so
  // the server-rendered `gift_active` flag can lag by minutes; this client
  // check prevents auto-adding a gift after the promo has technically ended.
  // When expired, an already-managed gift line is REMOVED (the matching
  // Shopify Buy-X-Get-Y discount has the same endsAt, so charging retail
  // for the auto-added line at checkout would be a worse outcome than just
  // pulling it).
  function isExpired() {
    if (!config.endsAtUnix) return false;
    return (Date.now() / 1000) > config.endsAtUnix;
  }

  var SECTIONS = [
    { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
  ];
  var SECTION_IDS = SECTIONS.map(function (s) { return s.section; });

  // Bumped by every in-drawer quantity change. A reconcile scheduled from an
  // older cart event is dropped when it finally runs: the quantity path has
  // already made the gift decision on a newer cart.
  var generation = 0;
  var pendingTimer = null;
  var pendingHtml = null;
  var pendingGeneration = 0;

  function runExclusive(task) {
    return window.ebCartMutex ? window.ebCartMutex(task) : task();
  }

  function getSectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /* Attribute-selector value: quotes and backslashes escaped. */
  function attrValue(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  /* Where focus should land if the swap below removes the focused control:
     the same control in the same keyed row (adding or removing the gift
     never changes another line's key, but does shift every positional id),
     else that row's name link; outside the rows, the element's own id. */
  function focusSuccessors(drawer) {
    var el = document.activeElement;
    if (!drawer || !el || !drawer.contains(el)) return [];
    var out = [];
    var row = el.closest('.cart-item[data-key]');
    if (row) {
      var rowSel = '.cart-item[data-key="' + attrValue(row.dataset.key) + '"]';
      var name = el.getAttribute('name');
      if (name) out.push(rowSel + ' ' + el.tagName.toLowerCase() + '[name="' + attrValue(name) + '"]');
      if (el.matches('[data-cart-sub-period], [data-cart-sub-upgrade]')) {
        out.push(rowSel + ' [data-cart-sub-period]', rowSel + ' [data-cart-sub-upgrade]');
      }
      out.push(rowSel + ' .cart-item__name');
    } else if (el.id) {
      out.push('[id="' + attrValue(el.id) + '"]');
    }
    return out;
  }

  function applySections(parsedState) {
    if (!parsedState || !parsedState.sections) return;
    var cartDrawer = document.querySelector('cart-drawer');
    var hadFocus = !!(cartDrawer && cartDrawer.contains(document.activeElement));
    var successors = hadFocus ? focusSuccessors(cartDrawer) : [];
    if (cartDrawer && typeof parsedState.item_count === 'number') {
      cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    }
    SECTIONS.forEach(function (s) {
      var html = parsedState.sections[s.section];
      if (!html) return;
      var target = document.getElementById(s.id);
      if (!target) return;
      var inner = target.querySelector(s.selector) || target;
      inner.innerHTML = getSectionInnerHTML(html, s.selector);
    });
    /* The swap dropped focus and stranded the drawer's focus trap on
       detached nodes; re-trap on the new markup (cart-drawer.js). */
    if (cartDrawer && typeof cartDrawer.refocusAfterRender === 'function') {
      cartDrawer.refocusAfterRender(hadFocus, successors);
    }
  }

  function notifyDownstream() {
    // Only dispatch the cart-drawer:rerender CustomEvent so the
    // recommendations widget re-hydrates. Intentionally do NOT publish
    // PUB_SUB_EVENTS.cartUpdate — our reconcile already applied the new
    // drawer sections to the DOM, and re-publishing would make
    // cart-items.onCartUpdate refetch the same cart-drawer section.
    document.dispatchEvent(new CustomEvent('cart-drawer:rerender', { detail: { source: 'gift-promo' } }));
  }

  /* The [data-cart-state] stamp from a rendered cart-drawer section. */
  function readState(drawerHtml) {
    if (!drawerHtml) return null;
    var doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    var el = doc.querySelector('[data-cart-state]');
    if (!el) return null;
    return {
      itemCount: parseInt(el.getAttribute('data-item-count'), 10) || 0,
      qualifying: parseInt(el.getAttribute('data-qualifying-total'), 10) || 0,
      // The managed (auto-added, `_free_gift`) line — the only line we ever
      // remove; a Hand Mist the customer added on purpose is never touched.
      giftKey: el.getAttribute('data-gift-key') || '',
      giftQty: parseInt(el.getAttribute('data-gift-qty'), 10) || 0,
      // Any line with the gift variant, managed or not — gates ADDITION, so
      // a manually-added one isn't duplicated (the discount makes it free).
      giftVariantInCart: el.getAttribute('data-gift-variant-in-cart') === '1'
    };
  }

  function decideMutation(state) {
    if (!state) return { type: 'none' };
    if (isExpired()) {
      return state.giftKey ? { type: 'change', key: state.giftKey, quantity: 0 } : { type: 'none' };
    }
    var meets = state.qualifying >= config.thresholdCents;
    if (meets && !state.giftVariantInCart) return { type: 'add' };
    if (!meets && state.giftKey) return { type: 'change', key: state.giftKey, quantity: 0 };
    // Two overlapping adds used to merge into quantity 2; the discount frees
    // only one ("max 1 per order") while the drawer labelled the line FREE.
    if (meets && state.giftKey && state.giftQty > 1) return { type: 'change', key: state.giftKey, quantity: 1 };
    return { type: 'none' };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (json) {
        if (!r.ok || !json || json.status >= 400 || json.errors) {
          var err = new Error((json && (json.description || json.message || json.errors)) || ('HTTP ' + r.status));
          err.response = json;
          throw err;
        }
        return json;
      });
    });
  }

  function addGift() {
    var props = {};
    props[GIFT_PROPERTY_KEY] = GIFT_PROPERTY_VALUE;
    return postJSON(window.routes.cart_add_url, {
      items: [{ id: config.variantId, quantity: 1, properties: props }],
      sections: SECTION_IDS,
      sections_url: window.location.pathname
    });
  }

  function changeGift(key, quantity) {
    return postJSON(window.routes.cart_change_url, {
      id: key,
      quantity: quantity,
      sections: SECTION_IDS,
      sections_url: window.location.pathname
    });
  }

  /* Perform a decided mutation; resolves to its response (with sections). */
  function perform(mutation) {
    if (mutation.type === 'add') return addGift();
    if (mutation.type === 'change') return changeGift(mutation.key, mutation.quantity);
    return Promise.resolve(null);
  }

  function fetchDrawerHtml() {
    return fetch(window.routes.cart_url + '?section_id=cart-drawer', {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then(function (r) { return r.ok ? r.text() : ''; });
  }

  /* Pubsub-path reconcile (PDP add, recommendation add, subscription change,
     candle vessel, quick order). Runs inside the cart queue. */
  function reconcile(html, scheduledGeneration) {
    if (scheduledGeneration !== generation) return null;
    var htmlPromise = html ? Promise.resolve(html) : fetchDrawerHtml();
    return htmlPromise.then(function (drawerHtml) {
      var mutation = decideMutation(readState(drawerHtml));
      if (mutation.type === 'none') return null;
      return perform(mutation);
    }).then(function (parsedState) {
      if (parsedState) {
        applySections(parsedState);
        notifyDownstream();
      }
    }).catch(function (err) {
      console.error('[gift-promo] reconcile failed', err);
    });
  }

  function scheduleReconcile(event) {
    var sections = event && event.cartData && event.cartData.sections;
    pendingHtml = (sections && sections['cart-drawer']) || null;
    pendingGeneration = generation;
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () {
      pendingTimer = null;
      var html = pendingHtml;
      var gen = pendingGeneration;
      pendingHtml = null;
      // Triggers that arrive while this one runs schedule another run, which
      // queues behind it — a trigger is never dropped.
      runExclusive(function () { return reconcile(html, gen); });
    }, RECONCILE_DEBOUNCE_MS);
  }

  function trySubscribePubsub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Skip our own publishes, and events whose source already ran the gift
      // decision inline (the drawer's quantity path — see below).
      if (event && event.source === 'gift-promo') return;
      if (event && event._giftHandled) return;
      scheduleReconcile(event);
    });
    return true;
  }

  /*
   * Inline gift decision inside the cart drawer's quantity path.
   *
   * The pubsub-driven reconcile (above) fires AFTER the quantity change has
   * rendered, then renders a second time when the gift is added/removed —
   * a visible flash, and a stale moment for cart-drawer-recommendations.js.
   * cart.js calls afterQuantityChange(state) between the /cart/change.js
   * response and the render, inside the cart queue; we fold the gift
   * mutation in there and hand back the state to render, so the shopper
   * sees ONE render. cart.js owns the request, the error handling, the
   * render and the publish (flagged _giftHandled so the pubsub path above
   * skips it).
   *
   * The base CartItems class (used on the /cart page) is not patched: that
   * page's line-item template has no gift-line treatment, so it falls
   * through to the pubsub reconcile.
   */
  function patchCartDrawerItems() {
    if (!window.customElements) return;
    customElements.whenDefined('cart-drawer-items').then(function () {
      var Cls = customElements.get('cart-drawer-items');
      if (!Cls || Cls.prototype._giftPatched) return;
      Cls.prototype._giftPatched = true;

      Cls.prototype.afterQuantityChange = function (state1) {
        generation++;
        if (pendingTimer) {
          clearTimeout(pendingTimer);
          pendingTimer = null;
        }
        state1.__giftHandled = true;
        var mutation = decideMutation(readState(state1.sections && state1.sections['cart-drawer']));
        if (mutation.type === 'none') return state1;

        return perform(mutation).then(function (result) {
          if (mutation.type === 'change') {
            result.__giftHandled = true;
            return result;
          }
          // /cart/add.js returns the added line(s) + sections, NOT the full
          // cart. Render its sections; for downstream subscribers synthesise
          // a close-enough cart by appending the added line to state1's
          // items (total_price stays state1's — the gift line is $0 once the
          // discount applies).
          var addedItems = result.items || (result.id ? [result] : []);
          return {
            __giftHandled: true,
            item_count: state1.item_count + addedItems.reduce(function (s, it) {
              return s + (it.quantity || 0);
            }, 0),
            items: (state1.items || []).concat(addedItems),
            sections: result.sections || state1.sections,
            total_price: state1.total_price
          };
        }).catch(function (err) {
          // The quantity change itself succeeded — render it rather than
          // leaving the drawer stale with no message. The next cart change
          // retries the gift decision.
          console.error('[gift-promo] gift ' + mutation.type + ' failed', err);
          return state1;
        });
      };
    });
  }

  /* ---------- cleanup mode (no active promotion; see the header) ---------- */

  /* The managed gift line's key on the rendered stamp ('' = none). In
     cleanup mode cart-drawer.liquid stamps the first `_free_gift` line. */
  function renderedGiftKey() {
    var el = document.querySelector('#CartDrawer [data-cart-state]');
    return el ? el.getAttribute('data-gift-key') || '' : '';
  }

  /* Through the drawer's existing visually-hidden role="status" region. It
     was just swapped in empty with the drawer; filling it a beat later is
     what screen readers announce. (Hidden with the rest of the form when the
     cart is now empty — the drawer's empty state then says it.) */
  function announce(message) {
    if (!message) return;
    setTimeout(function () {
      var region = document.getElementById('CartDrawer-LiveRegionText');
      if (region) region.textContent = message;
    }, 150);
  }

  var cleanupRun = null;

  /* Remove the leftover managed gift line(s) through the cart queue.
     Resolves to true when something was removed. */
  function removeLeftoverGift() {
    if (cleanupRun) return cleanupRun;
    if (!renderedGiftKey()) return Promise.resolve(false);
    var removed = false;
    cleanupRun = runExclusive(function () {
      // Read the key inside the queue (a change queued ahead of us may have
      // re-rendered the drawer). Each response's stamp names the next
      // leftover, if there's more than one; bounded so it can't loop.
      function step(key, remaining) {
        if (!key || remaining <= 0) return null;
        return changeGift(key, 0).then(function (parsedState) {
          removed = true;
          applySections(parsedState);
          var next = readState(parsedState.sections && parsedState.sections['cart-drawer']);
          return step(next && next.giftKey !== key ? next.giftKey : '', remaining - 1);
        });
      }
      return Promise.resolve(step(renderedGiftKey(), 3)).then(function () {
        if (removed) notifyDownstream();
      });
    }).catch(function (err) {
      // Usually a stale drawer (the key is gone). Re-read the cart so the
      // stamp is right for the next attempt; never retried in a loop.
      console.error('[gift-promo] leftover gift removal failed', err);
      if (removed) notifyDownstream();
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.resync === 'function') drawer.resync({ force: true });
    }).then(function () {
      cleanupRun = null;
      if (removed) announce(config.removedMessage);
      return removed;
    });
    return cleanupRun;
  }

  function startCleanup() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // When the drawer opens (any opener — watched on its `active` class, as
    // cart-drawer-recommendations.js does).
    var wasOpen = drawer.classList.contains('active');
    new MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open && !wasOpen) removeLeftoverGift();
      wasOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    if (wasOpen) removeLeftoverGift();

    // Before checkout: hold a Check out click while the line is still in the
    // cart, then click the re-rendered button. Capture phase, so the Scent
    // Society gate (a bubbling listener that skips prevented clicks) only sees
    // the released click. If the removal didn't work, stop holding — a stuck
    // Check out is worse than the line it was protecting against.
    var holdCheckout = true;
    document.addEventListener('click', function (e) {
      if (!holdCheckout) return;
      var btn = e.target && e.target.closest && e.target.closest('#CartDrawer-Checkout');
      if (!btn || btn.disabled || !renderedGiftKey()) return;
      e.preventDefault();
      e.stopPropagation();
      removeLeftoverGift().then(function () {
        if (renderedGiftKey()) holdCheckout = false;
        // Disabled = the leftover gift was the only line; nothing to check out.
        var fresh = document.getElementById('CartDrawer-Checkout');
        if (fresh && !fresh.disabled) fresh.click();
      });
    }, true);
  }

  if (cleanupOnly) {
    startCleanup();
    return;
  }

  patchCartDrawerItems();

  // Subscribe to cartUpdate for non-qty-stepper mutation paths (PDP ATC,
  // subscription change, recommendation tile ATC). Intentionally NO
  // page-load reconcile: the Liquid renders the cart with the correct
  // gift state server-side, so fetching on every page load is wasted work
  // that eats Shopify's per-IP request budget — visible during dev sessions
  // as 429s on the storefront once the budget is exhausted. The cost of
  // skipping page-load reconcile: if the cart drifts out of sync (rare —
  // would require a server-side state change between page renders), it
  // stays drifted until the next cart mutation.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trySubscribePubsub);
  } else if (!trySubscribePubsub()) {
    document.addEventListener('DOMContentLoaded', trySubscribePubsub);
  }
})();
